import { fabric } from "fabric";
import { v4 } from "uuid";
import store from "../../redux/store";
import { CanvasHandler } from "../../handlers";
import { FabricEvent, FabricObject,FabricGroup } from "../../types/FabricCustomType";
import { IObjectDesingner } from "../../types/HandlerObjectType";
import { RowProperties } from "./properties";
import { iconCheck } from "../../constants/Icons";
import { SeatFactory } from "./factories/seat.factory";
import { SeatShapeFactory, TextFactory } from "./factories";
import { ObjectSuperTypesEnum, ObjectTypesEnum } from "../../enums";
import { CURVE_MODE_RELATIVE } from "./helpers/RowSeatLayoutHelper";

class Row extends RowProperties implements IObjectDesingner {
  private defaultSeatColor: string;
  private rowSeatSpacing: number;
  private rowSeatSize: number;

  constructor(CanvasHandler: CanvasHandler) {
    super(CanvasHandler);
    this.rowSeatSpacing = 0;
  }
  onRotated(event: FabricEvent) {
    throw new Error("Method not implemented.");
  }

  public onInit() {
    this.canvasHandler.interactionHandler.drawing(ObjectSuperTypesEnum.Row, "crosshair");
    this.canvasHandler.activeShape = null;
    this.defaultSeatColor = "#000000";
  }

  onMouseDblClick(event: FabricEvent<Event>) {
    return;
  }

  public onMousedown(event: FabricEvent<MouseEvent>) {
    const {
      properties: { rowSeatSpacing, rowSeatSize },
    } = store.getState();
    this.rowSeatSpacing = rowSeatSpacing;
    this.rowSeatSize = rowSeatSize;
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    this.createPreviewGroup();
    this.canvasHandler.isDown = true;
    const seat = SeatShapeFactory.createSeatShape({
        left: x,
        top: y,
        rowSeatSize: this.rowSeatSize,
        defaultSeatColor: this.defaultSeatColor,
        editable: this.canvasHandler.editable,
        isPreview: true,
        id: v4(),
        container: this.canvasHandler.container.id,
        objectOption: this.canvasHandler.objectOption,
      });
    this.addSeatToPreviewGroup(seat);
  }

  public onMousemove(event: FabricEvent<MouseEvent>) {
    if (!this.canvasHandler.isDown) return;
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    this.updatePreviewRow(x, y);
  }

  public onMouseup(event: FabricEvent<MouseEvent>) {
    const canvasHeight = this.canvasHandler.canvas.getHeight();
    const canvasWidth = this.canvasHandler.canvas.getWidth();
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    const dx = x - this.canvasHandler.originPointer.x;
    const dy = y - this.canvasHandler.originPointer.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < 3 || event.pointer.x < 1 || event.pointer.y < 1 || event.pointer.x > canvasWidth || event.pointer.y > canvasHeight) {
      this.canvasHandler.isDown = false;
      this.canvasHandler.canvas.remove(this.canvasHandler.previewGroup);
      this.canvasHandler.previewGroup = {} as FabricGroup;
      return;
    }

    const adjusted = this.adjustRowToAxis(x, y);
    this.generateSeatLinear(adjusted.x, adjusted.y);
  }

  private adjustRowToAxis(x: number, y: number) {
    const x1 = this.canvasHandler.originPointer.x;
    const y1 = this.canvasHandler.originPointer.y;
    const distanceX = x - x1;
    const distanceY = y - y1;

    const tan = distanceY / distanceX;
    const angle = (Math.atan(tan) * 180) / Math.PI;

    const threshold = 2;
    let newX = x;
    let newY = y;

    if (Math.abs(angle) < threshold) {
      newY = y1;
    } else if (Math.abs(Math.abs(angle) - 90) < threshold) {
      newX = x1;
    }

    return { x: newX, y: newY };
  }

  private createPreviewGroup() {
    const { editable } = this.canvasHandler;
    const id = v4();
    this.canvasHandler.previewGroup = new fabric.Group(undefined, {
      left: this.canvasHandler.originPointer.x,
      top: this.canvasHandler.originPointer.y,
      originX: "left",
      originY: "top",
      hasControls: editable,
      hasBorders: editable,
      selectable: editable,
      type: ObjectTypesEnum.Group,
    }) as FabricObject<fabric.Group>;

    this.canvasHandler.previewGroup.set({
      id: id,
      superType: ObjectSuperTypesEnum.Row,
      centerTransform: true,
      seatsQuantity: null,
      maxSeatsQuantity: null,
      rowSize: null,
      seatsSpacing: 0,
      seatsCurve: 0,
      curveMode: CURVE_MODE_RELATIVE,
      distanceBetweenX: 0,
      distanceBetweenY: 0,
    });

    this.canvasHandler.canvas.add(this.canvasHandler.previewGroup);
  }

  private createSeatCounter(x: number, y: number, seatCount: string) {
    const editable = this.canvasHandler.editable;
    const text = TextFactory.create({
      left: x,
      top: y,
      fontSize: 13,
      editable,
      text: `Asientos: ${seatCount}`
    });
    return text;
  }

  private createRowGroup() {
    const { editable } = this.canvasHandler;
    const id = v4();
    this.canvasHandler.rowGroup = new fabric.Group(undefined, {
      left: this.canvasHandler.originPointer.x,
      top: this.canvasHandler.originPointer.y,
      originX: "left",
      originY: "top",
      hasControls: false,
      hasBorders: editable,
      selectable: editable,
      type: ObjectTypesEnum.Group,
      subTargetCheck: true,
      perPixelTargetFind: true,
    }) as FabricObject<fabric.Group>;

    this.canvasHandler.rowGroup.set({
      id: id,
      superType: ObjectSuperTypesEnum.Row,
      centerTransform: true,
      seatsQuantity: null,
      maxSeatsQuantity: null,
      rowSize: null,
      seatsSpacing: 0,
      seatsCurve: 0,
      curveMode: CURVE_MODE_RELATIVE,
      distanceBetweenX: 0,
      distanceBetweenY: 0,
      category: {
        key: 0,
        name: "",
      },
      seat: {
        key: 0,
        list: [] as Array<Number | String>,
        reverse: false,
        startsAt: 1,
      },
      section: {
        label: "",
        displayedLabel: "",
      },
      row: {
        enabled: false,
        label: "",
        displayedLabel: "",
        position: {
          left: false,
          right: false,
        },
      },
    });
    this.canvasHandler.canvas.add(this.canvasHandler.rowGroup);
  }

  private addSeatToRowGroup(obj: FabricObject<fabric.Group>) {
    this.canvasHandler.activeShape = obj;
    this.canvasHandler.rowGroup?.addWithUpdate(obj);
    this.canvasHandler.canvas.renderAll();
  }

  private addSeatToPreviewGroup(obj: FabricObject) {
    this.canvasHandler.activeShape = obj;
    this.canvasHandler.previewGroup?.addWithUpdate(obj);
    this.canvasHandler.canvas.renderAll();
  }

  private updatePreviewRow(x: number, y: number) {
    const groupObjects = this.canvasHandler.previewGroup?.getObjects();
    groupObjects?.forEach((el: any, index: number) => {
      if (index !== 0) this.canvasHandler.previewGroup.remove(el);
    });
    this.canvasHandler.previewGroup.remove();

    const origin = { ...this.canvasHandler.originPointer };
    const distanceX = x - origin.x;
    const distanceY = y - origin.y;

    const longDistance = Math.hypot(distanceX, distanceY);
    const seatSpacing = this.rowSeatSize + this.rowSeatSpacing * 2;
    let seatQuantity = Math.floor(longDistance / seatSpacing);
    seatQuantity = seatQuantity === 0 ? 1 : seatQuantity;

    const unitX = distanceX / longDistance;
    const unitY = distanceY / longDistance;
    for (let i = 1; i < seatQuantity; i++) {
      const seatX = origin.x + unitX * seatSpacing * i;
      const seatY = origin.y + unitY * seatSpacing * i;

      this.canvasHandler.activeShape = null;
      const seat = SeatShapeFactory.createSeatShape({
        left: seatX,
        top: seatY,
        rowSeatSize: this.rowSeatSize,
        defaultSeatColor: this.defaultSeatColor,
        editable: this.canvasHandler.editable,
        isPreview: true,
        id: v4(),
        container: this.canvasHandler.container.id,
        objectOption: this.canvasHandler.objectOption,
      });
      this.addSeatToPreviewGroup(seat);
    }

    const textY = origin.y < y ? origin.y - 20 : origin.y + 20;
    const textCount = this.createSeatCounter(origin.x, textY, seatQuantity + "");
    this.addSeatToPreviewGroup(textCount);
  }


  private async updateRow(x: number, y: number) {
    const x1 = this.canvasHandler.originPointer.x;
    const y1 = this.canvasHandler.originPointer.y;

    const distanceX = x - x1;
    const distanceY = y - y1;
    const longDistance = Math.hypot(distanceX, distanceY);

    const seatSpacing = this.rowSeatSize + this.rowSeatSpacing * 2;
    let seatQuantity = Math.floor(longDistance / seatSpacing);
    seatQuantity = seatQuantity === 0 ? 1 : seatQuantity;

    const unitX = distanceX / longDistance;
    const unitY = distanceY / longDistance;

    for (let i = 0; i < seatQuantity; i++) {
      const seatX = x1 + unitX * seatSpacing * i;
      const seatY = y1 + unitY * seatSpacing * i;

      this.canvasHandler.activeShape = null;
      this.canvasHandler.isDown = true;

      const seat = await SeatFactory.create({
        left: seatX,
        top: seatY,
        rowSeatSize: this.rowSeatSize,
        defaultSeatColor: this.defaultSeatColor,
        editable: this.canvasHandler.editable,
        isPreview: false,
        container: this.canvasHandler.container.id,
        objectOption: this.canvasHandler.objectOption,
        iconSvgString: iconCheck,
      });

      this.addSeatToRowGroup(seat);
    }

    this.canvasHandler.canvas.renderAll();

    this.finish(distanceX, distanceY, longDistance, false);
  }


  private generateSeatLinear(x: number, y: number) {
    this.createRowGroup();
    this.updateRow(x, y);
  }

  private finish(
    distanceBetweenX: number,
    distanceBetweenY: number,
    longDistance: number,
    type: boolean
  ) {
    const {
      properties: { zoneActive },
    } = store.getState();
    this.canvasHandler.canvas.remove(this.canvasHandler.previewGroup);
    this.canvasHandler.isDown = false;
    this.canvasHandler.activeShape = null;
    const seatsQuantity = Math.trunc(
      longDistance / (this.rowSeatSize + this.rowSeatSpacing * 2)
    );
    if (seatsQuantity > 0) {
      const maxSeatsQuantity = Math.trunc(longDistance / this.rowSeatSize);
      const rowSize = longDistance;
      const angle = (Math.atan2(distanceBetweenY, distanceBetweenX) * 180) / Math.PI;
      this.canvasHandler.rowGroup?.set("seatsQuantity", seatsQuantity);
      this.canvasHandler.rowGroup?.set("maxSeatsQuantity", maxSeatsQuantity);
      this.canvasHandler.rowGroup?.set("rowSize", rowSize);
      this.canvasHandler.rowGroup?.set("seatsSpacing", this.rowSeatSpacing);
      this.canvasHandler.rowGroup?.set("seatsCurve", 0);
      this.canvasHandler.rowGroup?.set("distanceBetweenX", distanceBetweenX);
      this.canvasHandler.rowGroup?.set("distanceBetweenY", distanceBetweenY);

      this.canvasHandler.rowGroup?.set("angleHelp", angle);
      // this.canvasHandler.group?.set('angle', angle);
      this.canvasHandler.rowGroup?.set("angleType", type);
      this.canvasHandler.rowGroup?.setCoords();
      this.canvasHandler.rowGroup?.addWithUpdate();
      this.canvasHandler.rowGroup?.set("hasControls", true);
      this.canvasHandler.rowGroup?.set("hoverCursor", "crosshair");
      // this.canvasHandler.rowGroup?.set("selectable", false);
      this.canvasHandler.rowGroup?.setControlsVisibility({
        tl: false, //top-left
        mt: false, // middle-top
        tr: false, //top-right
        ml: false, //middle-left
        mr: false, //middle-right
        bl: false, // bottom-left
        mb: false, //middle-bottom
        br: false, //bottom-right
        mtr: true,
      });
      if (this.canvasHandler.activeZone) {
        this.canvasHandler.rowGroup?.set("zoneID", this.canvasHandler.activeZone.item(0).id)
        this.canvasHandler.rowGroup?.set({
          section: {
            label: this.canvasHandler.activeZone.section.label,
            displayedLabel: this.canvasHandler.activeZone.section.displayedLabel
          }
        })
      }
      // this.canvasHandler.canvas.renderAll();
      if (!this.canvasHandler.transactionHandler.active) {
        this.canvasHandler.transactionHandler.save("add");
      }
      this.canvasHandler.onAdd(this.canvasHandler.rowGroup);
      this.canvasHandler.canvas.discardActiveObject();
      this.canvasHandler.rowGroup = {} as FabricGroup;
      this.canvasHandler.previewGroup = {} as FabricGroup;
    }
  }
}
export default Row;
